import styled from "styled-components";
import { NotebookPen, StickyNote } from "lucide-react";

import { theme } from "./theme";
import { useT } from "./i18n";

// Labeled note affordances, used on both the rack cells and the Beranda rows so FO learn ONE visual
// language: "FO Notes" (internal work notes, NotebookPen) and "Guest Notes" (what the guest/OTA wrote,
// StickyNote). Icons are reserved for these purposes only.
const NoteButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.18rem 0.5rem;
  border: 1px solid ${(props) => (props.$active ? theme.gold : theme.border)};
  border-radius: 7px;
  background: ${(props) => (props.$active ? theme.goldSoft : "#ffffff")};
  color: ${theme.ink700};
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    border-color: ${theme.gold};
    color: ${theme.goldDark};
  }
`;

// These buttons always sit inside a clickable box (rack cell / Beranda row) that opens the booking
// drawer. They swallow their OWN click so pressing a button never also opens the drawer — but only
// the button does, so empty space beside it falls through to the box (opens the drawer) as expected.
const swallow = (handler) => (event) => {
  event.stopPropagation();
  handler?.(event);
};

// "3/4" = unchecked/total. Yellow while anything is open; calm neutral once everything is checked.
const CountPill = styled.span`
  padding: 0.05rem 0.4rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: ${(props) => (props.$open ? "#92400e" : theme.muted)};
  background: ${(props) => (props.$open ? "#fef3c7" : theme.canvas)};
  border: 1px solid ${(props) => (props.$open ? "#fde68a" : theme.border)};
`;

const Dot = styled.span`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: #b45309;
`;

export const FoNotesButton = (props) => {
  const { t } = useT();
  const counts = props.counts;
  const hasNotes = counts && counts.total > 0;
  const open = hasNotes && counts.unchecked > 0;
  return (
    <NoteButton
      type="button"
      $active={open}
      title={t("fo_comments_title")}
      onClick={swallow(props.onClick)}
    >
      <NotebookPen size={13} aria-hidden="true" />
      {t("fo_notes_label")}
      {hasNotes ? (
        <CountPill $open={open}>
          {counts.unchecked}/{counts.total}
        </CountPill>
      ) : null}
    </NoteButton>
  );
};

export const GuestNotesButton = (props) => {
  const { t } = useT();
  return (
    <NoteButton
      type="button"
      $active={props.active}
      title={t("ota_note_title")}
      onClick={swallow(props.onClick)}
    >
      <StickyNote size={13} aria-hidden="true" />
      {t("guest_notes_label")}
      {props.hasNote ? <Dot /> : null}
    </NoteButton>
  );
};
